import React from 'react';
import { Box, Button, IconButton, useTheme } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import TourIcon from '@mui/icons-material/Tour';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { alpha } from '@mui/material/styles';
import { layoutGap } from '../../../theme/theme';
import { exportActionTooltips } from '../../../shared/config/actionHelpCatalog';
import ActionHelpTooltip from '../../../shared/ui/common/ActionHelpTooltip';
import type { ExportRowHighlightColor, ExportRowHighlightMap } from '../lib/rowHighlights';
import type { PipingSystem } from '../types';
import type { GroupBy } from '../../../shared/types/pcfExport.types';
import { EXPORT_COLUMN_PANEL_ID } from '../exportPcfConstants';
import SelectedRowsHighlightColorMenu from './SelectedRowsHighlightColorMenu';
import PcfVisualCheckMenu from './PcfVisualCheckMenu';
import {
  getExportRailActionButtonSx,
  getExportRailQuietIconButtonSx,
} from './exportRailControlStyles';

interface ExportWorkspaceActionsProps {
  showGuideButton: boolean;
  systems: PipingSystem[];
  selected: Set<string>;
  rowHighlightColors: ExportRowHighlightMap;
  loading: boolean;
  systemsStale: boolean;
  narrow: boolean;
  columnPanelOpen: boolean;
  refreshButtonRef: React.Ref<HTMLButtonElement>;
  rowHighlightButtonRef: React.Ref<HTMLButtonElement>;
  visualCheckButtonRef?: React.Ref<HTMLButtonElement>;
  manageColumnsButtonRef: React.Ref<HTMLButtonElement>;
  exportSettingsButtonRef: React.Ref<HTMLButtonElement>;
  onOpenTour: () => void;
  onOpenGuide: () => void;
  onRefreshSystems: () => void;
  visualCheckBusy?: boolean;
  groupingParameterName?: string;
  groupingParameterSource?: 'Element' | 'Type';
  onApplyVisualCheck?: (mode: GroupBy) => void;
  onResetVisualCheck?: () => void;
  onRowHighlightColorChange: (
    systemName: string,
    color: ExportRowHighlightColor | null
  ) => void;
  onRowHighlightReset: () => void;
  onToggleColumnPanel: () => void;
  onOpenSettings: () => void;
}

export default function ExportWorkspaceActions({
  showGuideButton,
  systems,
  selected,
  rowHighlightColors,
  loading,
  systemsStale,
  narrow,
  columnPanelOpen,
  refreshButtonRef,
  rowHighlightButtonRef,
  visualCheckButtonRef,
  manageColumnsButtonRef,
  exportSettingsButtonRef,
  onOpenTour,
  onOpenGuide,
  onRefreshSystems,
  visualCheckBusy = false,
  groupingParameterName = '',
  groupingParameterSource = 'Element',
  onApplyVisualCheck = () => undefined,
  onResetVisualCheck = () => undefined,
  onRowHighlightColorChange,
  onRowHighlightReset,
  onToggleColumnPanel,
  onOpenSettings,
}: ExportWorkspaceActionsProps) {
  const theme = useTheme();
  const iconButtonSx = getExportRailQuietIconButtonSx(theme);

  return (
    <Box
      data-testid="export-pcf-review-actions"
      aria-label="PCF export review tools"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: layoutGap.dense,
        flexWrap: 'nowrap',
        minWidth: 0,
        '& > *': { flexShrink: 0 },
      }}
    >
      {showGuideButton ? (
        <>
          <ActionHelpTooltip help={exportActionTooltips.workspaceTour}>
            <IconButton
              size="small"
              onClick={onOpenTour}
              aria-label="Start PCF export tour"
              sx={iconButtonSx}
            >
              <TourIcon fontSize="small" />
            </IconButton>
          </ActionHelpTooltip>
          {!narrow ? (
            <ActionHelpTooltip help={exportActionTooltips.pageGuide}>
              <Button
                variant="text"
                size="small"
                onClick={onOpenGuide}
                aria-label="Page guide"
                sx={getExportRailActionButtonSx(theme, 'quiet')}
              >
                Guide
              </Button>
            </ActionHelpTooltip>
          ) : null}
        </>
      ) : null}
      <ActionHelpTooltip
        help={exportActionTooltips.refreshSystems}
        disabledHelp={exportActionTooltips.refreshSystemsDisabled}
        disabled={loading}
      >
        <IconButton
          ref={refreshButtonRef}
          size="small"
          onClick={onRefreshSystems}
          disabled={loading}
          aria-label={systemsStale ? 'Refresh piping systems after model change' : 'Refresh piping systems'}
          color={systemsStale ? 'warning' : 'default'}
          sx={iconButtonSx}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </ActionHelpTooltip>
      <PcfVisualCheckMenu
        busy={visualCheckBusy}
        parameterName={groupingParameterName}
        parameterSource={groupingParameterSource}
        buttonRef={visualCheckButtonRef}
        onApply={onApplyVisualCheck}
        onReset={onResetVisualCheck}
      />
      <SelectedRowsHighlightColorMenu
        systems={systems}
        selected={selected}
        rowHighlightColors={rowHighlightColors}
        compact
        buttonRef={rowHighlightButtonRef}
        onChange={onRowHighlightColorChange}
        onReset={onRowHighlightReset}
      />
      <ActionHelpTooltip help={exportActionTooltips.manageColumns}>
        <IconButton
          ref={manageColumnsButtonRef}
          size="small"
          onClick={onToggleColumnPanel}
          aria-label={columnPanelOpen ? 'Close manage columns panel' : 'Open manage columns panel'}
          aria-expanded={columnPanelOpen}
          aria-controls={EXPORT_COLUMN_PANEL_ID}
          sx={[
            iconButtonSx,
            {
              backgroundColor: columnPanelOpen
                ? alpha(theme.palette.primary.main, 0.08)
                : 'transparent',
            },
          ]}
        >
          <ViewColumnIcon fontSize="small" />
        </IconButton>
      </ActionHelpTooltip>
      <ActionHelpTooltip help={exportActionTooltips.exportSettings}>
        <IconButton
          ref={exportSettingsButtonRef}
          size="small"
          onClick={onOpenSettings}
          aria-label="Open export settings"
          sx={iconButtonSx}
        >
          <SettingsIcon fontSize="small" />
        </IconButton>
      </ActionHelpTooltip>
    </Box>
  );
}
